import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";
import { useMenuCategories } from "@/services/menu/menuItem/fetchMenuItems";

interface CategorySelectorProps {
  selectedCategoryIds: string[];
  onCategoriesChange: (categoryIds: string[]) => void;
  className?: string;
  storeId?: string;
}

export function CategorySelector({
  selectedCategoryIds,
  onCategoriesChange,
  className,
  storeId,
}: CategorySelectorProps) {
  const { t } = useTranslation();
  const { data: categories = [], isLoading, error } = useMenuCategories(storeId);

  // Create category options from loaded categories
  const categoryOptions = React.useMemo(() => {
    return categories.map((category) => ({
      label: category.name,
      value: category.id,
    }));
  }, [categories]);

  // Ensure selectedCategoryIds is always an array
  const safeSelectedIds = Array.isArray(selectedCategoryIds)
    ? selectedCategoryIds
    : [];

  const handleCategoryToggle = (categoryId: string, isChecked: boolean) => {
    if (isChecked) {
      onCategoriesChange([...safeSelectedIds, categoryId]);
    } else {
      onCategoriesChange(safeSelectedIds.filter((id) => id !== categoryId));
    }
  };

  if (isLoading) {
    return (
      <div className="h-10 animate-pulse bg-muted rounded-md">
        {t("categorySelector.loading")}
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-destructive">{error.message}</div>;
  }

  if (categories.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        {t("categorySelector.noCategories")}
      </div>
    );
  }

  return (
    <div
      className={`border rounded-md w-full p-2 max-h-40 overflow-y-auto ${
        className || ""
      }`}
    >
      <div className="flex flex-wrap gap-3">
        {categoryOptions.map((category) => (
          <div key={category.value} className="flex items-center gap-2">
            <Checkbox
              id={`category-${category.value}`}
              checked={safeSelectedIds.includes(category.value)}
              onCheckedChange={(checked) =>
                handleCategoryToggle(category.value, checked === true)
              }
            />
            <label
              htmlFor={`category-${category.value}`}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {category.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
